import React, { memo, useMemo } from "react";
import "./AccountCard.scss";
import classNames from "classnames";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import {
    getAccountNameForConfirmation,
    getAccountNameWithLast4,
    getLeadingAccountConnection,
} from "@common/helpers/financialAccount";
import { FinancialAccountBase } from "@common/types/financialAccount";
import { FinancialConnection } from "@common/types/financialConnection";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { useIdCopy } from "@hooks/useIdCopy";
import { AccountInstitutionIcon } from "@components/institution/AccountInstitutionIcon";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { getAccountClassificationLabel } from "../helpers";

export interface AccountCardProps {
    account: FinancialAccountBase;
    connection?: FinancialConnection | IntegrationConnection;
    showNumber?: boolean;
    onlyNumber?: boolean;
    numberInName?: boolean;
    showType?: boolean;
    className?: string;
    iconSize?: "xs" | "sm" | "md";
    maxNameLength?: number;
    accountNameConfirmationMode?: boolean;
    displayNameAsTooltip?: boolean;
    skipTextBreak?: boolean;
    hideIcon?: boolean;
}

export const AccountCard: React.FC<AccountCardProps> = memo(
    ({
        account,
        connection: connectionProp,
        showNumber,
        numberInName,
        className,
        showType = true,
        onlyNumber = false,
        iconSize = "md",
        maxNameLength,
        accountNameConfirmationMode,
        displayNameAsTooltip,
        skipTextBreak,
        hideIcon,
    }) => {
        const { getCopyProps } = useIdCopy({ label: "Financial Account" });
        const connection =
            connectionProp ?? getLeadingAccountConnection(account) ?? undefined;

        let label: string | null = null;

        if (showNumber && account.accountNumberMask) {
            label = getAnonymousAccountNumber(account.accountNumberMask);
        } else if (showType) {
            label = getAccountClassificationLabel(account);
        }

        let displayedName: string = useMemo(() => {
            if (accountNameConfirmationMode) {
                return getAccountNameForConfirmation(account);
            }
            if (!account.accountNumberMask) {
                return account.name ?? "";
            }
            if (onlyNumber) {
                return getAnonymousAccountNumber(account.accountNumberMask, 0);
            }
            if (numberInName) {
                return getAccountNameWithLast4(account, 2);
            }

            return account.name ?? "";
        }, [account, onlyNumber, numberInName, accountNameConfirmationMode]);

        if (maxNameLength && displayedName.length > maxNameLength) {
            displayedName = displayedName.slice(0, maxNameLength) + "...";
        }

        return (
            <div
                className={classNames("account-card", className, {
                    "account-card--only-number":
                        onlyNumber && account.accountNumberMask,
                })}
            >
                {!hideIcon && (
                    <AccountInstitutionIcon
                        account={account}
                        connection={connection}
                        size={iconSize}
                        className={classNames(
                            "account-card__icon",
                            `account-card__icon--${iconSize}`,
                        )}
                    />
                )}
                <div className="account-card__name-type">
                    <Tooltip
                        content={
                            displayNameAsTooltip ? account.name : undefined
                        }
                    >
                        <div
                            className="account-card__name"
                            data-testid="account-name"
                        >
                            {skipTextBreak && (
                                <span
                                    className="account-card__name__full"
                                    {...getCopyProps(account.id)}
                                >
                                    {displayedName}
                                </span>
                            )}
                            {!skipTextBreak && (
                                <BreakableText
                                    text={displayedName}
                                    copyId={account.id}
                                    copyLabel="Financial Account"
                                />
                            )}
                        </div>
                    </Tooltip>
                    {!onlyNumber && !accountNameConfirmationMode && (
                        <div className="account-card__type">{label}</div>
                    )}
                </div>
            </div>
        );
    },
);
AccountCard.displayName = "AccountCard";
